<template>
  <div v-show="isCustomFieldValid">
    <label
      v-if="!props.hideLabel && !props.isTableCell"
      class="input-label tw-flex tw-items-center"
      :for="`${customField?.name}`"
    >
      {{ customField?.name }}
      <span
        v-if="props.customField.required"
        class="tw-ml-0.5 tw-text-sm tw-text-red"
      >
        *
      </span>
      <help-text
        v-if="!!customField.description && !props.hideLabel"
        class="tw-mb-0.5 tw-ml-1"
      >
        {{ customField.description }}
      </help-text>
    </label>
    <template v-if="showInput">
      <div v-if="props.hideLabel" class="url-input-group">
        <q-input
          :id="`${customField?.name}`"
          v-model="localVal"
          :for="`${customField?.name}`"
          :name="`${customField?.name}`"
          dense
          :readonly="props.readonly"
          outlined
          class="input-xs url-input-group__input tw-bg-white"
          :class="{
            'form-input--error': !!errorMessage && showErrorMessage,
          }"
          type="url"
          :placeholder="props.readonly ? '' : 'Enter a URL'"
          @blur="onBlur"
          @keydown.enter="handleEnter"
          @keydown.stop.escape="handleEscape"
        />
        <p-btn
          size="sm"
          outlined
          class="url-input-group__button"
          :title="
            localVal && isValidUrl
              ? 'Open link in new tab'
              : 'Enter a valid URL'
          "
          :disabled="!localVal || !isValidUrl"
          @click.stop="openLink"
        >
          <octicon
            icon="link-external"
            :width="16"
            :height="16"
            :class="
              localVal && isValidUrl ? 'tw-text-blue' : 'tw-text-gray-medium'
            "
          />
        </p-btn>
      </div>
      <q-input
        v-else
        :id="`${customField?.name}`"
        v-model="localVal"
        :for="`${customField?.name}`"
        :name="`${customField?.name}`"
        dense
        :readonly="props.readonly"
        outlined
        class="input-xs tw-w-full tw-bg-white"
        :class="{
          'form-input--error': !!errorMessage && showErrorMessage,
        }"
        type="url"
        :placeholder="props.readonly ? '' : 'Enter a URL'"
        @blur="onBlur"
        @keydown.enter="handleEnter"
        @keydown.stop.escape="handleEscape"
      />
      <span
        v-if="!!errorMessage && showErrorMessage"
        class="form-input__error-message"
      >
        {{ errorMessage }}
      </span>
    </template>
    <template v-else>
      <div
        v-if="props.isTableCell"
        class="tw-flex tw-min-h-full tw-w-full tw-items-center"
      >
        <div class="tw-flex tw-w-full tw-items-center tw-justify-between">
          <tooltip-text
            :text="props.value || '&mdash;'"
            max-width="calc(100% - 2rem)"
            class="tw-min-w-0 tw-flex-1"
          >
            <span class="tw-text-sm tw-text-gray-dark">
              {{ props.value || '&mdash;' }}
            </span>
          </tooltip-text>

          <p-btn
            v-if="props.value"
            size="sm"
            flat
            class="url-table-cell__button tw-ml-auto tw-shrink-0"
            title="Open link in new tab"
            @click.stop="openTableLink"
          >
            <octicon
              icon="link-external"
              :width="16"
              :height="16"
              class="tw-text-blue"
            />
          </p-btn>
        </div>
      </div>
      <div v-else class="tw-flex tw-w-full tw-items-center">
        <tooltip-text
          :text="props.value || ''"
          max-width="100%"
          class="tw-flex-1"
        >
          <a
            :href="navigableUrl(props.value || '')"
            target="_blank"
            class="primary-link tw-text-xs"
            @click.stop
          >
            {{ props.value }}
          </a>
        </tooltip-text>

        <div class="tw-ml-auto tw-flex tw-items-center tw-gap-1">
          <p-btn
            size="sm"
            flat
            class="url-table-cell__button"
            title="Open link in new tab"
            @click.stop="openTableLink"
          >
            <octicon
              icon="link-external"
              :width="16"
              :height="16"
              class="tw-text-blue"
            />
          </p-btn>

          <octicon
            v-if="!props.readonly"
            icon="pencil"
            class="tw-shrink-0 tw-cursor-pointer tw-text-gray-medium-dark hover:tw-opacity-70"
            :width="16"
            :height="16"
            @click.stop="manualShowInput = true"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { escapeURL } from '@/utils/helpers';
import isURL from 'validator/lib/isURL';
import { ref, computed, watch } from 'vue';
import TooltipText from '../core/TooltipText.vue';
import HelpText from '../design-system/misc/HelpText.vue';
import { CustomField, CustomFieldTextSubType, CustomFieldType } from '@/api';

interface Props {
  customField: CustomField & { required?: boolean };
  value?: string;
  hideLabel?: boolean;
  readonly?: boolean;
  isTableCell?: boolean;
}
interface Emits {
  (e: 'value-updated', value: string | null): void;
  (e: 'enter'): void;
  (e: 'escape'): void;
}

const props = withDefaults(defineProps<Props>(), {});
const emit = defineEmits<Emits>();

// In modal context (hideLabel=true), always show input
const showInput = computed(() => {
  // If in table cell, never show input (always show view mode)
  if (props.isTableCell) return false;
  // If in modal (hideLabel is true), always show input
  if (props.hideLabel) return true;
  // Otherwise, show input if no value or if manually triggered
  return !props.value || manualShowInput.value;
});

const manualShowInput = ref(false);

const isCustomFieldValid = computed(() => {
  return (
    props.customField.type === CustomFieldType.TEXT &&
    props.customField.subType === CustomFieldTextSubType.URL
  );
});

const localVal = ref<string>(props.value || '');
watch(
  () => props.value,
  () => {
    localVal.value = props.value || '';
  }
);

const showErrorMessage = ref(false);

const isValidUrl = computed(() => {
  if (!localVal.value) return false;
  return isURL(escapeURL(localVal.value), {
    require_protocol: false,
    require_valid_protocol: false,
  });
});

const errorMessage = computed(() => {
  if (!localVal.value && props.customField.required) {
    return 'This field is required';
  }
  if (!localVal.value) {
    return '';
  }
  if (!isValidUrl.value) {
    return 'Invalid URL';
  }
  return '';
});

function navigableUrl(url: string) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `http://${url}`;
}

watch(localVal, () => {
  showErrorMessage.value = false;
});

function onBlur() {
  if (props.customField.required && (!localVal.value || !!errorMessage.value)) {
    if (!props.hideLabel) manualShowInput.value = false;
    localVal.value = props.value || '';
    return;
  }
  onValueChange();
}

function onValueChange() {
  if (errorMessage.value) {
    showErrorMessage.value = true;
    return;
  }
  if (!props.hideLabel) manualShowInput.value = false;

  // Normalize URL by adding https:// if protocol is missing
  let normalizedValue = localVal.value;
  if (normalizedValue && !normalizedValue.match(/^[a-zA-Z]+:\/\//)) {
    normalizedValue = `https://${normalizedValue}`;
  }

  if (normalizedValue === props.value || (!normalizedValue && !props.value)) {
    return;
  }
  emit('value-updated', normalizedValue || null);
}

function handleEnter() {
  // Normalize URL by adding https:// if protocol is missing
  let normalizedValue = localVal.value;
  if (normalizedValue && !normalizedValue.match(/^[a-zA-Z]+:\/\//)) {
    normalizedValue = `https://${normalizedValue}`;
  }

  // Emit the normalized value before emitting enter
  if (normalizedValue !== props.value) {
    emit('value-updated', normalizedValue);
  }
  emit('enter');
}

function handleEscape() {
  localVal.value = props.value || '';
  emit('escape');
}

function openLink() {
  if (localVal.value && isValidUrl.value) {
    window.open(navigableUrl(localVal.value), '_blank');
  }
}

function openTableLink() {
  if (props.value) {
    window.open(navigableUrl(props.value), '_blank');

    // Return focus to the parent cell for keyboard navigation after a short delay
    // This ensures the click event completes before focus changes
    setTimeout(() => {
      const button = document.activeElement as HTMLElement;
      const parentCell = button?.closest(
        '[data-col-identifier]'
      ) as HTMLElement;

      if (parentCell) {
        parentCell.focus();
      }
    }, 0);
  }
}

const isValid = computed(() => {
  return !errorMessage.value;
});

function validate() {
  showErrorMessage.value = !isValid.value;
}

function removeError() {
  showErrorMessage.value = false;
}

defineExpose({
  validate,
  removeError,
  isValid,
});
</script>

<style lang="scss" scoped>
.url-input-group {
  @apply tw-flex tw-items-stretch tw-gap-2;

  &__input {
    @apply tw-flex-1;
  }

  &__button {
    @apply tw-shrink-0 tw-rounded;
    min-width: 40px;
    height: auto;

    :deep(.q-btn__content) {
      @apply tw-flex tw-items-center tw-justify-center;
    }

    // Enabled state
    &:not([disabled]) {
      border: 1px solid rgb(var(--tw-color-gray-medium)) !important;

      &:hover {
        background-color: var(--q-primary) !important;
        color: white !important;

        :deep(.octicon) {
          color: white !important;
        }
      }
    }

    // Disabled state
    &[disabled] {
      @apply tw-cursor-not-allowed;
      border: 1px solid rgb(var(--tw-color-gray-medium)) !important;
      opacity: 1 !important;

      :deep(.q-btn__content) {
        opacity: 1 !important;
      }
    }
  }
}

.url-table-cell__button {
  @apply hover:tw-bg-gray-light tw-rounded;
  height: 24px;
  &:focus {
    @apply tw-outline-2 tw-outline-offset-1 tw-outline-primary;
  }
}
</style>
